import React, { useState } from "react";
import styled from "styled-components";
import { FaTimes } from "react-icons/fa";
import useStepWizard from "../utils/hooks/useStepWizard";
import { safeNavigateBack } from "../utils/routing";
import SEO from "../components/SEO";
import { buildForm } from "../components/Order/schema";
import Section, { Nav } from "../components/Order/form";
import DefaultProgress from "../components/Progress";
import Button from "../components/FormNew/Button";
import logo from "../images/greek-dressing-logo-white.svg";
import illustration from "../images/tshirt-rack.svg";
import FormNetlify from "../components/Form/FormNetlify";
import { encode } from "../utils/form";

const Container = styled.div`
  width: 100vw;
  min-height: 100vh;
  display: flex;
  flex-direction: column;

  @media (min-width: 900px) {
    flex-direction: row;
  }
`;

const Sidebar = styled.div`
  background: ${({ theme }) => theme.colors.primary};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin: 0;
  flex: 1;
  padding: 13vh 60px;
  color: ${({ theme }) => theme.colors.white};
  position: relative;

  /* Animation */
  transform: translateY(-100vh);
  animation: slide 500ms ease-in-out;
  animation-delay: 300ms;
  animation-fill-mode: forwards;

  img {
    &:first-child {
      position: absolute;
      top: 0;
      width: 100%;
      width: 110px;
      left: 15px;
      margin-left: 0;
    }
  }

  h2 {
    font-size: 2.5rem;
    color: ${({ theme }) => theme.colors.white};

    /* Animation */
    opacity: 0;
    transform: translateY(5vh);
    animation: opacitySlide 300ms ease-in-out;
    animation-delay: 800ms;
    animation-fill-mode: forwards;
  }

  p {
    font-weight: 500;
    font-size: 1.2rem;

    /* Animation */
    opacity: 0;
    transform: translateY(8vh);
    animation: opacitySlide 300ms ease-in-out;
    animation-delay: 800ms;
    animation-fill-mode: forwards;
  }

  @media (min-width: 900px) {
    max-width: 550px;

    /* Animation */
    transform: translateX(-50vw);

    img {
      &:first-child {
        left: 50%;
        margin-left: -60px;
      }
    }
  }

  @keyframes slide {
    100% {
      transform: translate(0);
    }
  }

  @keyframes opacitySlide {
    100% {
      transform: translate(0);
      opacity: 1;
    }
  }
`;

const FormContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  flex: 2;
  margin: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Form = styled.div`
  max-width: 500px;
  width: 100%;

  /* Animation */
  transform: translateY(100vh);
  animation: slide 500ms ease-in-out;
  animation-delay: 700ms;
  animation-fill-mode: forwards;

  @media (min-width: 900px) {
    /* Animation */
    transform: translateX(100vw);
  }

  @keyframes slide {
    90% {
      transform: scale(1.1);
    }

    100% {
      transform: scale(0);
      transform: translateX(0);
    }
  }
`;

const Progress = styled(DefaultProgress)`
  margin-bottom: 20px;
`;

const Summary = styled.div`
  text-align: center;
  height: 100%;
  min-height: 50vh;
  display: flex;
  flex-direction: column;

  div {
    &:first-child {
      display: flex;
      flex-direction: column;
      justify-content: center;
      flex: 100%;
      max-width: 400px;
      margin: auto;
    }
  }

  h2 {
    font-size: 2rem;
  }

  h5 {
    color: ${({ theme }) => theme.colors.gray};
    font-weight: 400;
    font-size: 1.2rem;

    span {
      color: ${({ theme }) => theme.colors.black};
    }
  }
`;

const Illustration = styled.img`
  max-width: 300px;

  /* Animation */
  transform: scale(0);
  animation: scale 400ms ease-in-out;
  animation-delay: 1000ms;
  animation-fill-mode: forwards;

  @keyframes scale {
    90% {
      transform: scale(1.05);
    }

    100% {
      transform: scale(1);
    }
  }
`;

const Close = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  padding: 15px;
  background: ${({ theme }) => theme.colors.white};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  cursor: pointer;
  transition: all 200ms ease-in-out;
  z-index: 100;

  &:hover {
    transform: scale(1.1);

    svg {
      transition: all 200ms ease-in-out;
      transform: scale(1.3);
    }
  }

  @media (min-width: 900px) {
    background: ${({ theme }) => theme.colors.lightGray};
  }
`;

export default function NewOrder() {
  const { currentStep, nextStep, previousStep } = useStepWizard();
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const netlifyFormName = "new-order-2";

  function setForm(data) {
    setFormData((prev) => ({
      ...prev,
      ...data,
    }));
  }

  function handleNextStep(data) {
    setForm(data);
    nextStep();
  }

  async function submitToNetlify(data) {
    try {
      const res = await fetch("/", {
        method: "POST",
        body: encode({ ...data, "form-name": netlifyFormName }),
      });

      if (!res || !res.ok) {
        throw new Error(
          "There was a problem with your form submission. Please try again!"
        );
      }
    } catch (e) {
      throw e;
    }
  }

  function handleSubmit(data) {
    setForm(data);
    setIsLoading(true);

    const hydratedDate = { ...formData, ...data };

    submitToNetlify(hydratedDate);
    setTimeout(() => {
      nextStep();
      setIsLoading(false);
    }, 1200); // Lets animation effect run a bit longer
  }

  const orderForms = buildForm(previousStep, handleNextStep, handleSubmit);

  return (
    <Container>
      <SEO
        title="New Order - Greek Dressing"
        description="Order the freshest custom apparel for any college organization. We offer online group ordering, free mockups from our design team, and rapidly fast delivery!"
      />
      <Close onClick={safeNavigateBack}>
        <FaTimes size="1.5rem" />
      </Close>
      {/* This is needed by Netlify to pre-register the form fields. This form is hidden and never actually used */}
      <FormNetlify name={netlifyFormName} hidden>
        {orderForms.map((form) =>
          form.fields.map((field) => <input hidden name={field.name} />)
        )}
      </FormNetlify>
      <Sidebar>
        <img src={logo} alt="Greek Dressing Logo" title="Greek Dressing Logo" />
        <Illustration
          src={illustration}
          alt="Custom T-Shirts"
          title="Make Custom T-Shirts"
        />
        <h2>Products you'll love</h2>
        <p>
          We work with all types of organizations to create stunning apparel and
          promotional products. Our designs are always 100% free.
        </p>
      </Sidebar>
      <FormContainer>
        <Form>
          <Progress length={orderForms.length} current={currentStep - 1} />
          {orderForms.map((form, index) => {
            if (currentStep === index + 1) {
              return (
                <Section
                  form={form}
                  defaultData={formData}
                  key={form.name}
                  loading={isLoading}
                />
              );
            }
          })}
          {currentStep === orderForms.length + 1 && (
            <Summary>
              <div>
                <h2>{`Thanks for the details ${formData.name}!`}</h2>
                <h5>
                  {`We will contat you shorly at the following email address to get your design started: `}
                  <span>{formData.email}</span>.
                </h5>
              </div>
              <Nav>
                <Button type="button" onClick={safeNavigateBack}>
                  Finish
                </Button>
              </Nav>
            </Summary>
          )}
        </Form>
      </FormContainer>
    </Container>
  );
}
